import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { Tour } from "@/data/tours";
import { trackAddToCart, trackRemoveFromCart } from "@/lib/analytics";

export interface CartItem {
  tour: Tour;
  quantity: number;
  participants: number;
}

interface CartContextType {
  items: CartItem[];
  addToCart: (tour: Tour, participants?: number) => void;
  removeFromCart: (tourId: string) => void;
  updateQuantity: (tourId: string, quantity: number) => void;
  updateParticipants: (tourId: string, participants: number) => void;
  clearCart: () => void;
  isInCart: (tourId: string) => boolean;
  totalItems: number;
  isCartOpen: boolean;
  setIsCartOpen: (open: boolean) => void;
  hasNewClientDiscount: boolean;
  applyNewClientDiscount: () => void;
  removeNewClientDiscount: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = "splora-cart";
const NEW_CLIENT_DISCOUNT_KEY = "splora-new-client-discount";

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [hasNewClientDiscount, setHasNewClientDiscount] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    if (stored) {
      try {
        setItems(JSON.parse(stored));
      } catch {
        localStorage.removeItem(CART_STORAGE_KEY);
      }
    }
    // Load new client discount state
    const discountStored = localStorage.getItem(NEW_CLIENT_DISCOUNT_KEY);
    if (discountStored === "true") {
      setHasNewClientDiscount(true);
    }
  }, []);

  // Save cart to localStorage on change
  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addToCart = (tour: Tour, participants = 2) => {
    // Track add to cart event
    trackAddToCart(tour, 1);
    
    setItems((prev) => {
      const existing = prev.find((item) => item.tour.id === tour.id);
      if (existing) {
        return prev.map((item) =>
          item.tour.id === tour.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { tour, quantity: 1, participants }];
    });
    setIsCartOpen(true);
  };

  const removeFromCart = (tourId: string) => {
    // Track remove from cart event
    const item = items.find((i) => i.tour.id === tourId);
    if (item) {
      trackRemoveFromCart(item.tour, item.quantity);
    }
    
    setItems((prev) => prev.filter((item) => item.tour.id !== tourId));
  };

  const updateQuantity = (tourId: string, quantity: number) => {
    if (quantity < 1) {
      removeFromCart(tourId);
      return;
    }
    setItems((prev) =>
      prev.map((item) =>
        item.tour.id === tourId ? { ...item, quantity } : item
      )
    );
  };

  const updateParticipants = (tourId: string, participants: number) => {
    if (participants < 1) return;
    setItems((prev) =>
      prev.map((item) =>
        item.tour.id === tourId ? { ...item, participants } : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
    localStorage.removeItem(CART_STORAGE_KEY);
  };

  const isInCart = (tourId: string) => {
    return items.some((item) => item.tour.id === tourId);
  };

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  const applyNewClientDiscount = () => {
    setHasNewClientDiscount(true);
    localStorage.setItem(NEW_CLIENT_DISCOUNT_KEY, "true");
  };

  const removeNewClientDiscount = () => {
    setHasNewClientDiscount(false);
    localStorage.removeItem(NEW_CLIENT_DISCOUNT_KEY);
  };

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        removeFromCart,
        updateQuantity,
        updateParticipants,
        clearCart,
        isInCart,
        totalItems,
        isCartOpen,
        setIsCartOpen,
        hasNewClientDiscount,
        applyNewClientDiscount,
        removeNewClientDiscount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
